/**
 * Governed Orchestration Playbook Templates
 *
 * This file exports pre-built workflow templates that can be loaded into the canvas.
 * Each playbook is a JSON definition with nodes and connections representing a complete
 * security workflow for various incident response, access control, and compliance scenarios.
 *
 * Production-quality templates with realistic data flows and configurations.
 */

export const PLAYBOOK_CATEGORIES = [
  {
    id: 'identity-access',
    name: 'Identity & Access',
    description: 'User access, authentication, and account management workflows',
    color: '#7C3AED',
    icon: '👤',
    count: 3,
  },
  {
    id: 'incident-response',
    name: 'Incident Response',
    description: 'Security incident detection, triage, and remediation workflows',
    color: '#DC2626',
    icon: '🚨',
    count: 3,
  },
  {
    id: 'compliance-audit',
    name: 'Compliance & Audit',
    description: 'Compliance evidence collection and policy violation remediation',
    color: '#059669',
    icon: '✓',
    count: 2,
  },
  {
    id: 'platform-ops',
    name: 'Platform Operations',
    description: 'System workflows for user management, notifications, and platform automation',
    color: '#0d9488',
    icon: '⚙',
    count: 1,
  },
  {
    id: 'data-protection',
    name: 'Data Protection',
    description: 'PII redaction, policy enforcement, compliance scoring, and audit trail workflows',
    color: '#dc2626',
    icon: '🛡',
    count: 3,
  },
];

export const SOAR_PLAYBOOKS = [
  // ============================================================================
  // IDENTITY & ACCESS PLAYBOOKS
  // ============================================================================

  {
    id: 'suspicious_login_response',
    name: 'Suspicious Login Response',
    description: 'Automatically respond to suspicious login attempts by analyzing geo-location anomalies and disabling accounts if needed',
    category: 'identity-access',
    severity: 'high',
    tags: ['authentication', 'geolocation', 'account-security', 'auto-response'],
    icon: '🔐',
    estimatedDuration: '5-10 minutes',
    requiredConnectors: ['identity-provider', 'geo-intelligence', 'slack'],

    nodes: [
      {
        id: 'trigger_suspicious_login',
        type: 'webhook',
        label: 'Suspicious Login Alert',
        x: 0,
        y: 0,
        config: {
          webhookPath: '/webhooks/suspicious-login',
          method: 'POST',
          outputVariable: 'loginAlert',
          description: 'Receives alerts from identity provider about suspicious login attempts',
        },
      },
      {
        id: 'lookup_user',
        type: 'user_lookup',
        label: 'Lookup User Details',
        x: 200,
        y: 0,
        config: {
          inputMapping: {
            userId: 'loginAlert.userId',
            attributes: ['lastLoginLocation', 'lastLoginIP', 'accountStatus', 'mfaEnabled'],
          },
          outputVariable: 'userDetails',
          connector: 'identity-provider',
        },
      },
      {
        id: 'check_geo_anomaly',
        type: 'threat_intel_lookup',
        label: 'Check Geo Anomaly',
        x: 400,
        y: 0,
        config: {
          inputMapping: {
            currentLocation: 'loginAlert.loginLocation',
            previousLocation: 'userDetails.lastLoginLocation',
            timeDifference: 'loginAlert.timeSinceLastLogin',
          },
          lookupType: 'geo-velocity',
          outputVariable: 'geoAnalysis',
          description: 'Analyzes if the login distance is physically possible',
          connector: 'geo-intelligence',
        },
      },
      {
        id: 'is_suspicious',
        type: 'condition',
        label: 'Is Suspicious?',
        x: 600,
        y: 0,
        config: {
          inputMapping: {
            anomalyScore: 'geoAnalysis.anomalyScore',
            isImpossibleVelocity: 'geoAnalysis.isImpossibleVelocity',
          },
          conditions: [
            {
              id: 'cond_high_risk',
              expression: 'anomalyScore > 0.7 || isImpossibleVelocity',
              label: 'High Risk',
              outputVariable: 'suspiciousRisk',
            },
          ],
          defaultOutput: 'normalActivity',
        },
      },
      {
        id: 'disable_account',
        type: 'user_action',
        label: 'Disable Account',
        x: 800,
        y: -100,
        config: {
          inputMapping: {
            userId: 'userDetails.userId',
            reason: 'Suspicious login activity detected',
          },
          action: 'disable',
          outputVariable: 'disableResult',
          requiresApproval: false,
          connector: 'identity-provider',
        },
      },
      {
        id: 'notify_soc',
        type: 'notification_task',
        label: 'Notify SOC Team',
        x: 800,
        y: 0,
        config: {
          inputMapping: {
            userId: 'userDetails.userId',
            loginLocation: 'loginAlert.loginLocation',
            anomalyScore: 'geoAnalysis.anomalyScore',
            accountAction: 'disableResult.status',
          },
          channels: ['slack', 'email'],
          template: 'suspicious_login_soc_alert',
          outputVariable: 'notificationResult',
          slackChannel: '#security-alerts',
          severity: 'critical',
        },
      },
      {
        id: 'log_normal_activity',
        type: 'system_task',
        label: 'Log Normal Activity',
        x: 800,
        y: 100,
        config: {
          inputMapping: {
            userId: 'userDetails.userId',
            loginLocation: 'loginAlert.loginLocation',
            eventType: 'normal_login',
          },
          action: 'log',
          destination: 'audit-log',
          outputVariable: 'auditLog',
        },
      },
      {
        id: 'close_incident',
        type: 'system_task',
        label: 'Close & Complete',
        x: 1000,
        y: 0,
        config: {
          action: 'complete-workflow',
          outputVariable: 'workflowComplete',
        },
      },
    ],

    connections: [
      {
        id: 'conn_trigger_to_lookup',
        from: 'trigger_suspicious_login',
        fromPort: 'output',
        to: 'lookup_user',
        toPort: 'input',
      },
      {
        id: 'conn_lookup_to_geo',
        from: 'lookup_user',
        fromPort: 'output',
        to: 'check_geo_anomaly',
        toPort: 'input',
      },
      {
        id: 'conn_geo_to_condition',
        from: 'check_geo_anomaly',
        fromPort: 'output',
        to: 'is_suspicious',
        toPort: 'input',
      },
      {
        id: 'conn_suspicious_yes',
        from: 'is_suspicious',
        fromPort: 'highRisk',
        to: 'disable_account',
        toPort: 'input',
      },
      {
        id: 'conn_suspicious_no',
        from: 'is_suspicious',
        fromPort: 'normalActivity',
        to: 'log_normal_activity',
        toPort: 'input',
      },
      {
        id: 'conn_disable_to_notify',
        from: 'disable_account',
        fromPort: 'output',
        to: 'notify_soc',
        toPort: 'input',
      },
      {
        id: 'conn_notify_to_close',
        from: 'notify_soc',
        fromPort: 'output',
        to: 'close_incident',
        toPort: 'input',
      },
      {
        id: 'conn_log_to_close',
        from: 'log_normal_activity',
        fromPort: 'output',
        to: 'close_incident',
        toPort: 'input',
      },
    ],
  },

  {
    id: 'user_offboarding',
    name: 'User Offboarding Automation',
    description: 'Automated user offboarding workflow with parallel tasks for revoking access, disabling accounts, and compliance reporting',
    category: 'identity-access',
    severity: 'medium',
    tags: ['offboarding', 'access-revocation', 'compliance', 'parallel-execution'],
    icon: '👋',
    estimatedDuration: '15-20 minutes',
    requiredConnectors: ['identity-provider', 'google-workspace', 'github', 'slack'],

    nodes: [
      {
        id: 'manual_start_offboard',
        type: 'manual_start',
        label: 'Start Offboarding',
        x: 0,
        y: 0,
        config: {
          inputSchema: {
            userId: 'string',
            userName: 'string',
            offboardingDate: 'date',
            transferToManager: 'string',
          },
          outputVariable: 'offboardingRequest',
        },
      },
      {
        id: 'lookup_user_details',
        type: 'user_lookup',
        label: 'Lookup User Details',
        x: 200,
        y: 0,
        config: {
          inputMapping: {
            userId: 'offboardingRequest.userId',
            attributes: ['email', 'department', 'manager', 'accessLevel', 'systems'],
          },
          outputVariable: 'userInfo',
          connector: 'identity-provider',
        },
      },
      {
        id: 'parallel_fork',
        type: 'parallel_fork',
        label: 'Parallel Access Revocation',
        x: 400,
        y: 0,
        config: {
          branches: 4,
          outputVariable: 'parallelStart',
        },
      },
      {
        id: 'revoke_oauth_tokens',
        type: 'user_action',
        label: 'Revoke OAuth Tokens',
        x: 500,
        y: -150,
        config: {
          inputMapping: {
            userId: 'userInfo.userId',
            email: 'userInfo.email',
            revokeAll: true,
          },
          action: 'revoke-tokens',
          outputVariable: 'tokenRevocation',
          connector: 'identity-provider',
        },
      },
      {
        id: 'disable_account',
        type: 'user_action',
        label: 'Disable Account',
        x: 500,
        y: -50,
        config: {
          inputMapping: {
            userId: 'userInfo.userId',
            reason: 'User offboarding',
            suspendLogins: true,
          },
          action: 'disable',
          outputVariable: 'accountDisable',
          connector: 'identity-provider',
        },
      },
      {
        id: 'transfer_data',
        type: 'connector_task',
        label: 'Transfer Data to Manager',
        x: 500,
        y: 50,
        config: {
          inputMapping: {
            sourceUserId: 'userInfo.userId',
            targetUserId: 'offboardingRequest.transferToManager',
            includeItems: ['files', 'calendar', 'emails'],
          },
          connector: 'google-workspace',
          action: 'transfer-account-data',
          outputVariable: 'dataTransfer',
        },
      },
      {
        id: 'remove_from_groups',
        type: 'user_action',
        label: 'Remove from Groups',
        x: 500,
        y: 150,
        config: {
          inputMapping: {
            userId: 'userInfo.userId',
            removeAll: true,
          },
          action: 'remove-from-groups',
          outputVariable: 'groupRemoval',
          connector: 'identity-provider',
        },
      },
      {
        id: 'parallel_join',
        type: 'parallel_join',
        label: 'Join Results',
        x: 700,
        y: 0,
        config: {
          expectedBranches: 4,
          waitForAll: true,
          outputVariable: 'revocationComplete',
        },
      },
      {
        id: 'generate_compliance_report',
        type: 'system_task',
        label: 'Generate Compliance Report',
        x: 900,
        y: 0,
        config: {
          inputMapping: {
            userId: 'userInfo.userId',
            revokedTokens: 'tokenRevocation.count',
            accountDisabled: 'accountDisable.timestamp',
            dataTransferred: 'dataTransfer.itemCount',
            groupsRemoved: 'groupRemoval.groupCount',
            offboardingDate: 'offboardingRequest.offboardingDate',
          },
          action: 'generate-report',
          template: 'offboarding-compliance',
          outputVariable: 'complianceReport',
          destination: 'compliance-vault',
        },
      },
      {
        id: 'notify_hr',
        type: 'notification_task',
        label: 'Notify HR Team',
        x: 1100,
        y: 0,
        config: {
          inputMapping: {
            userName: 'userInfo.userName',
            userId: 'userInfo.userId',
            reportId: 'complianceReport.reportId',
          },
          channels: ['email', 'slack'],
          template: 'offboarding_complete_notification',
          recipients: 'hr-team@company.com',
          slackChannel: '#hr-notifications',
          outputVariable: 'hrNotification',
        },
      },
      {
        id: 'close_offboarding',
        type: 'system_task',
        label: 'Close Offboarding',
        x: 1300,
        y: 0,
        config: {
          action: 'complete-workflow',
          outputVariable: 'workflowComplete',
        },
      },
    ],

    connections: [
      {
        id: 'conn_start_to_lookup',
        from: 'manual_start_offboard',
        fromPort: 'output',
        to: 'lookup_user_details',
        toPort: 'input',
      },
      {
        id: 'conn_lookup_to_fork',
        from: 'lookup_user_details',
        fromPort: 'output',
        to: 'parallel_fork',
        toPort: 'input',
      },
      {
        id: 'conn_fork_to_oauth',
        from: 'parallel_fork',
        fromPort: 'branch0',
        to: 'revoke_oauth_tokens',
        toPort: 'input',
      },
      {
        id: 'conn_fork_to_disable',
        from: 'parallel_fork',
        fromPort: 'branch1',
        to: 'disable_account',
        toPort: 'input',
      },
      {
        id: 'conn_fork_to_transfer',
        from: 'parallel_fork',
        fromPort: 'branch2',
        to: 'transfer_data',
        toPort: 'input',
      },
      {
        id: 'conn_fork_to_remove_groups',
        from: 'parallel_fork',
        fromPort: 'branch3',
        to: 'remove_from_groups',
        toPort: 'input',
      },
      {
        id: 'conn_oauth_to_join',
        from: 'revoke_oauth_tokens',
        fromPort: 'output',
        to: 'parallel_join',
        toPort: 'input0',
      },
      {
        id: 'conn_disable_to_join',
        from: 'disable_account',
        fromPort: 'output',
        to: 'parallel_join',
        toPort: 'input1',
      },
      {
        id: 'conn_transfer_to_join',
        from: 'transfer_data',
        fromPort: 'output',
        to: 'parallel_join',
        toPort: 'input2',
      },
      {
        id: 'conn_remove_to_join',
        from: 'remove_from_groups',
        fromPort: 'output',
        to: 'parallel_join',
        toPort: 'input3',
      },
      {
        id: 'conn_join_to_report',
        from: 'parallel_join',
        fromPort: 'output',
        to: 'generate_compliance_report',
        toPort: 'input',
      },
      {
        id: 'conn_report_to_notify',
        from: 'generate_compliance_report',
        fromPort: 'output',
        to: 'notify_hr',
        toPort: 'input',
      },
      {
        id: 'conn_notify_to_close',
        from: 'notify_hr',
        fromPort: 'output',
        to: 'close_offboarding',
        toPort: 'input',
      },
    ],
  },

  {
    id: 'access_review_automation',
    name: 'Access Review Automation',
    description: 'Quarterly access review workflow that identifies stale accounts and manages approval gates for account disabling',
    category: 'identity-access',
    severity: 'low',
    tags: ['access-review', 'compliance', 'scheduled', 'loop', 'approval'],
    icon: '🔍',
    estimatedDuration: '30-60 minutes',
    requiredConnectors: ['identity-provider', 'jira', 'slack'],

    nodes: [
      {
        id: 'schedule_quarterly',
        type: 'schedule',
        label: 'Quarterly Schedule',
        x: 0,
        y: 0,
        config: {
          cronExpression: '0 0 1 1,4,7,10 *',
          timezone: 'UTC',
          description: 'Runs quarterly on the first day of Q1, Q2, Q3, Q4 at 00:00 UTC',
          outputVariable: 'scheduleTriggered',
        },
      },
      {
        id: 'list_all_users',
        type: 'connector_task',
        label: 'List All Users',
        x: 200,
        y: 0,
        config: {
          connector: 'identity-provider',
          action: 'list-users',
          filters: {
            status: 'active',
            excludeServiceAccounts: true,
          },
          outputVariable: 'userList',
          batchSize: 100,
        },
      },
      {
        id: 'loop_users',
        type: 'loop',
        label: 'For Each User',
        x: 400,
        y: 0,
        config: {
          inputMapping: {
            items: 'userList.users',
          },
          loopVariable: 'currentUser',
          outputVariable: 'loopResults',
        },
      },
      {
        id: 'check_last_login',
        type: 'threat_intel_lookup',
        label: 'Check Last Login',
        x: 600,
        y: -100,
        config: {
          inputMapping: {
            userId: 'currentUser.userId',
          },
          lookupType: 'user-activity',
          days: 90,
          outputVariable: 'loginData',
          connector: 'identity-provider',
        },
      },
      {
        id: 'is_stale_account',
        type: 'condition',
        label: 'Last Login > 90 Days?',
        x: 800,
        y: -100,
        config: {
          inputMapping: {
            lastLogin: 'loginData.lastLoginDate',
            daysInactive: 'loginData.daysInactive',
          },
          conditions: [
            {
              id: 'cond_stale',
              expression: 'daysInactive > 90',
              label: 'Stale',
              outputVariable: 'isStale',
            },
          ],
          defaultOutput: 'active',
        },
      },
      {
        id: 'flag_for_review',
        type: 'system_task',
        label: 'Flag for Review',
        x: 1000,
        y: -150,
        config: {
          inputMapping: {
            userId: 'currentUser.userId',
            userName: 'currentUser.userName',
            lastLogin: 'loginData.lastLoginDate',
            daysInactive: 'loginData.daysInactive',
          },
          action: 'create-review-ticket',
          destination: 'jira',
          ticketType: 'Access Review',
          outputVariable: 'reviewTicket',
        },
      },
      {
        id: 'approval_gate',
        type: 'approval_gate',
        label: 'Approval Gate',
        x: 1200,
        y: -150,
        config: {
          inputMapping: {
            ticketId: 'reviewTicket.ticketId',
            userId: 'currentUser.userId',
            lastLogin: 'loginData.lastLoginDate',
          },
          approvalGroups: ['security-team', 'access-reviewers'],
          timeout: 604800,
          outputVariable: 'approvalDecision',
          escalateIfNoResponse: true,
        },
      },
      {
        id: 'decision_gate',
        type: 'switch_case',
        label: 'Decision',
        x: 1400,
        y: -150,
        config: {
          inputMapping: {
            decision: 'approvalDecision.decision',
          },
          cases: [
            {
              id: 'case_disable',
              value: 'disable',
              label: 'Disable Account',
              outputVariable: 'disableDecision',
            },
            {
              id: 'case_keep',
              value: 'keep',
              label: 'Keep Active',
              outputVariable: 'keepDecision',
            },
          ],
          defaultCase: 'keep',
        },
      },
      {
        id: 'disable_account',
        type: 'user_action',
        label: 'Disable Stale Account',
        x: 1600,
        y: -200,
        config: {
          inputMapping: {
            userId: 'currentUser.userId',
            reason: 'Access review: no activity for 90+ days',
          },
          action: 'disable',
          outputVariable: 'disableResult',
          connector: 'identity-provider',
        },
      },
      {
        id: 'keep_active',
        type: 'system_task',
        label: 'Keep Active',
        x: 1600,
        y: -100,
        config: {
          inputMapping: {
            userId: 'currentUser.userId',
          },
          action: 'log-decision',
          destination: 'audit-log',
          outputVariable: 'keepActive',
        },
      },
      {
        id: 'skip_active_user',
        type: 'system_task',
        label: 'Skip Active User',
        x: 1000,
        y: 100,
        config: {
          inputMapping: {
            userId: 'currentUser.userId',
          },
          action: 'log',
          destination: 'audit-log',
          outputVariable: 'skipResult',
        },
      },
      {
        id: 'merge_results',
        type: 'parallel_join',
        label: 'Merge Results',
        x: 1800,
        y: 0,
        config: {
          expectedBranches: 3,
          waitForAll: false,
          outputVariable: 'mergedResults',
        },
      },
      {
        id: 'generate_report',
        type: 'system_task',
        label: 'Generate Access Review Report',
        x: 2000,
        y: 0,
        config: {
          inputMapping: {
            totalUsers: 'userList.totalCount',
            staleAccounts: 'loopResults.staleCount',
            disabledAccounts: 'loopResults.disabledCount',
            activeAccounts: 'loopResults.activeCount',
            reviewPeriod: 'quarterly',
          },
          action: 'generate-report',
          template: 'access-review-report',
          outputVariable: 'accessReviewReport',
          destination: 'compliance-vault',
        },
      },
      {
        id: 'notify_team',
        type: 'notification_task',
        label: 'Notify Security Team',
        x: 2200,
        y: 0,
        config: {
          inputMapping: {
            reportId: 'accessReviewReport.reportId',
            staleCount: 'loopResults.staleCount',
            disabledCount: 'loopResults.disabledCount',
          },
          channels: ['slack', 'email'],
          template: 'access_review_complete',
          slackChannel: '#security-operations',
          recipients: 'security-team@company.com',
          outputVariable: 'notification',
        },
      },
      {
        id: 'complete_review',
        type: 'system_task',
        label: 'Complete Review',
        x: 2400,
        y: 0,
        config: {
          action: 'complete-workflow',
          outputVariable: 'workflowComplete',
        },
      },
    ],

    connections: [
      {
        id: 'conn_schedule_to_list',
        from: 'schedule_quarterly',
        fromPort: 'output',
        to: 'list_all_users',
        toPort: 'input',
      },
      {
        id: 'conn_list_to_loop',
        from: 'list_all_users',
        fromPort: 'output',
        to: 'loop_users',
        toPort: 'input',
      },
      {
        id: 'conn_loop_to_check',
        from: 'loop_users',
        fromPort: 'loopStart',
        to: 'check_last_login',
        toPort: 'input',
      },
      {
        id: 'conn_check_to_condition',
        from: 'check_last_login',
        fromPort: 'output',
        to: 'is_stale_account',
        toPort: 'input',
      },
      {
        id: 'conn_stale_yes',
        from: 'is_stale_account',
        fromPort: 'stale',
        to: 'flag_for_review',
        toPort: 'input',
      },
      {
        id: 'conn_stale_no',
        from: 'is_stale_account',
        fromPort: 'active',
        to: 'skip_active_user',
        toPort: 'input',
      },
      {
        id: 'conn_flag_to_approval',
        from: 'flag_for_review',
        fromPort: 'output',
        to: 'approval_gate',
        toPort: 'input',
      },
      {
        id: 'conn_approval_to_decision',
        from: 'approval_gate',
        fromPort: 'output',
        to: 'decision_gate',
        toPort: 'input',
      },
      {
        id: 'conn_decision_disable',
        from: 'decision_gate',
        fromPort: 'caseDisable',
        to: 'disable_account',
        toPort: 'input',
      },
      {
        id: 'conn_decision_keep',
        from: 'decision_gate',
        fromPort: 'caseKeep',
        to: 'keep_active',
        toPort: 'input',
      },
      {
        id: 'conn_disable_to_merge',
        from: 'disable_account',
        fromPort: 'output',
        to: 'merge_results',
        toPort: 'input0',
      },
      {
        id: 'conn_keep_to_merge',
        from: 'keep_active',
        fromPort: 'output',
        to: 'merge_results',
        toPort: 'input1',
      },
      {
        id: 'conn_skip_to_merge',
        from: 'skip_active_user',
        fromPort: 'output',
        to: 'merge_results',
        toPort: 'input2',
      },
      {
        id: 'conn_loop_end',
        from: 'merge_results',
        fromPort: 'output',
        to: 'loop_users',
        toPort: 'loopEnd',
      },
      {
        id: 'conn_loop_to_report',
        from: 'loop_users',
        fromPort: 'loopComplete',
        to: 'generate_report',
        toPort: 'input',
      },
      {
        id: 'conn_report_to_notify',
        from: 'generate_report',
        fromPort: 'output',
        to: 'notify_team',
        toPort: 'input',
      },
      {
        id: 'conn_notify_to_complete',
        from: 'notify_team',
        fromPort: 'output',
        to: 'complete_review',
        toPort: 'input',
      },
    ],
  },

  // ============================================================================
  // INCIDENT RESPONSE PLAYBOOKS
  // ============================================================================

  {
    id: 'phishing_email_triage',
    name: 'Phishing Email Triage',
    description: 'Automated phishing email analysis using IOC extraction, threat intelligence lookups, and LLM-based severity classification',
    category: 'incident-response',
    severity: 'critical',
    tags: ['phishing', 'email-security', 'ioc-extraction', 'threat-intelligence', 'auto-response'],
    icon: '🎣',
    estimatedDuration: '10-15 minutes',
    requiredConnectors: ['email-security', 'virustotal', 'openai', 'slack'],

    nodes: [
      {
        id: 'email_alert',
        type: 'email_trigger',
        label: 'Email Alert',
        x: 0,
        y: 0,
        config: {
          source: 'email-security',
          alertType: 'suspicious-email',
          outputVariable: 'emailAlert',
          description: 'Triggered by email security gateway on suspicious email detection',
        },
      },
      {
        id: 'extract_iocs',
        type: 'script_task',
        label: 'Extract IOCs',
        x: 200,
        y: 0,
        config: {
          inputMapping: {
            emailBody: 'emailAlert.body',
            emailSubject: 'emailAlert.subject',
            senderEmail: 'emailAlert.sender',
            attachments: 'emailAlert.attachments',
          },
          scriptType: 'ioc-extraction',
          extractPatterns: ['urls', 'hashes', 'ips', 'domains'],
          outputVariable: 'extractedIOCs',
        },
      },
      {
        id: 'parallel_threat_lookups',
        type: 'parallel_fork',
        label: 'Parallel Threat Lookups',
        x: 400,
        y: 0,
        config: {
          branches: 3,
          outputVariable: 'parallelStart',
        },
      },
      {
        id: 'virustotal_lookup',
        type: 'threat_intel_lookup',
        label: 'VirusTotal Lookup',
        x: 550,
        y: -150,
        config: {
          inputMapping: {
            hashes: 'extractedIOCs.hashes',
            urls: 'extractedIOCs.urls',
          },
          connector: 'virustotal',
          lookupType: 'reputation',
          outputVariable: 'virustotalResults',
          batchSize: 10,
        },
      },
      {
        id: 'url_reputation',
        type: 'threat_intel_lookup',
        label: 'URL Reputation Lookup',
        x: 550,
        y: 0,
        config: {
          inputMapping: {
            urls: 'extractedIOCs.urls',
            domains: 'extractedIOCs.domains',
          },
          lookupType: 'url-reputation',
          outputVariable: 'urlReputation',
          connector: 'email-security',
        },
      },
      {
        id: 'sender_history',
        type: 'threat_intel_lookup',
        label: 'Sender History Lookup',
        x: 550,
        y: 150,
        config: {
          inputMapping: {
            senderEmail: 'emailAlert.sender',
            senderDomain: 'extractedIOCs.senderDomain',
          },
          lookupType: 'sender-reputation',
          outputVariable: 'senderHistory',
          connector: 'email-security',
        },
      },
      {
        id: 'join_lookups',
        type: 'parallel_join',
        label: 'Join Lookups',
        x: 750,
        y: 0,
        config: {
          expectedBranches: 3,
          waitForAll: true,
          outputVariable: 'allLookups',
        },
      },
      {
        id: 'classify_severity',
        type: 'llm_task',
        label: 'LLM Classify Severity',
        x: 950,
        y: 0,
        config: {
          inputMapping: {
            emailBody: 'emailAlert.body',
            subject: 'emailAlert.subject',
            virustotalDetections: 'virustotalResults.detectionRatio',
            urlRepScore: 'urlReputation.riskScore',
            senderRep: 'senderHistory.reputation',
            iocs: 'extractedIOCs',
          },
          connector: 'openai',
          model: 'gpt-4',
          systemPrompt: 'You are a security analyst. Classify the phishing email severity based on provided indicators. Respond with JSON: {severity: "critical"|"high"|"medium"|"low", confidence: 0-1, reasoning: string, indicatorsFound: []}',
          outputVariable: 'severityClassification',
          temperature: 0.3,
        },
      },
      {
        id: 'is_critical',
        type: 'condition',
        label: 'Is Critical?',
        x: 1150,
        y: 0,
        config: {
          inputMapping: {
            severity: 'severityClassification.severity',
            confidence: 'severityClassification.confidence',
          },
          conditions: [
            {
              id: 'cond_critical',
              expression: 'severity === "critical" && confidence > 0.8',
              label: 'Critical',
              outputVariable: 'isCritical',
            },
          ],
          defaultOutput: 'notCritical',
        },
      },
      {
        id: 'quarantine_email',
        type: 'connector_task',
        label: 'Quarantine Email',
        x: 1350,
        y: -150,
        config: {
          inputMapping: {
            messageId: 'emailAlert.messageId',
            reason: 'Critical phishing email',
          },
          connector: 'email-security',
          action: 'quarantine-message',
          outputVariable: 'quarantineResult',
        },
      },
      {
        id: 'block_sender',
        type: 'connector_task',
        label: 'Block Sender',
        x: 1350,
        y: -50,
        config: {
          inputMapping: {
            senderEmail: 'emailAlert.sender',
            blockType: 'domain' | 'address',
          },
          connector: 'email-security',
          action: 'block-sender',
          outputVariable: 'blockResult',
        },
      },
      {
        id: 'notify_critical',
        type: 'notification_task',
        label: 'Notify Security Team',
        x: 1350,
        y: 50,
        config: {
          inputMapping: {
            severity: 'severityClassification.severity',
            senderEmail: 'emailAlert.sender',
            iocs: 'extractedIOCs',
            detections: 'virustotalResults.detectionRatio',
          },
          channels: ['slack', 'email'],
          template: 'phishing_critical_alert',
          slackChannel: '#security-incidents',
          recipients: 'security-team@company.com',
          outputVariable: 'criticalNotification',
        },
      },
      {
        id: 'flag_and_log',
        type: 'system_task',
        label: 'Flag & Log',
        x: 1350,
        y: 150,
        config: {
          inputMapping: {
            severity: 'severityClassification.severity',
            senderEmail: 'emailAlert.sender',
            iocs: 'extractedIOCs',
          },
          action: 'log-and-flag',
          destination: 'audit-log',
          outputVariable: 'logResult',
        },
      },
      {
        id: 'create_incident',
        type: 'incident_create',
        label: 'Create Incident',
        x: 1550,
        y: -50,
        config: {
          inputMapping: {
            title: 'Phishing Email Detected',
            severity: 'severityClassification.severity',
            description: 'severityClassification.reasoning',
            iocs: 'extractedIOCs',
          },
          incidentType: 'phishing',
          outputVariable: 'incidentCreated',
          connector: 'jira',
        },
      },
      {
        id: 'close_analysis',
        type: 'system_task',
        label: 'Close & Complete',
        x: 1750,
        y: 0,
        config: {
          action: 'complete-workflow',
          outputVariable: 'workflowComplete',
        },
      },
    ],

    connections: [
      {
        id: 'conn_alert_to_extract',
        from: 'email_alert',
        fromPort: 'output',
        to: 'extract_iocs',
        toPort: 'input',
      },
      {
        id: 'conn_extract_to_fork',
        from: 'extract_iocs',
        fromPort: 'output',
        to: 'parallel_threat_lookups',
        toPort: 'input',
      },
      {
        id: 'conn_fork_to_vt',
        from: 'parallel_threat_lookups',
        fromPort: 'branch0',
        to: 'virustotal_lookup',
        toPort: 'input',
      },
      {
        id: 'conn_fork_to_url',
        from: 'parallel_threat_lookups',
        fromPort: 'branch1',
        to: 'url_reputation',
        toPort: 'input',
      },
      {
        id: 'conn_fork_to_sender',
        from: 'parallel_threat_lookups',
        fromPort: 'branch2',
        to: 'sender_history',
        toPort: 'input',
      },
      {
        id: 'conn_vt_to_join',
        from: 'virustotal_lookup',
        fromPort: 'output',
        to: 'join_lookups',
        toPort: 'input0',
      },
      {
        id: 'conn_url_to_join',
        from: 'url_reputation',
        fromPort: 'output',
        to: 'join_lookups',
        toPort: 'input1',
      },
      {
        id: 'conn_sender_to_join',
        from: 'sender_history',
        fromPort: 'output',
        to: 'join_lookups',
        toPort: 'input2',
      },
      {
        id: 'conn_join_to_llm',
        from: 'join_lookups',
        fromPort: 'output',
        to: 'classify_severity',
        toPort: 'input',
      },
      {
        id: 'conn_llm_to_condition',
        from: 'classify_severity',
        fromPort: 'output',
        to: 'is_critical',
        toPort: 'input',
      },
      {
        id: 'conn_critical_yes',
        from: 'is_critical',
        fromPort: 'critical',
        to: 'quarantine_email',
        toPort: 'input',
      },
      {
        id: 'conn_critical_no',
        from: 'is_critical',
        fromPort: 'notCritical',
        to: 'flag_and_log',
        toPort: 'input',
      },
      {
        id: 'conn_quarantine_to_block',
        from: 'quarantine_email',
        fromPort: 'output',
        to: 'block_sender',
        toPort: 'input',
      },
      {
        id: 'conn_block_to_notify',
        from: 'block_sender',
        fromPort: 'output',
        to: 'notify_critical',
        toPort: 'input',
      },
      {
        id: 'conn_notify_to_incident',
        from: 'notify_critical',
        fromPort: 'output',
        to: 'create_incident',
        toPort: 'input',
      },
      {
        id: 'conn_incident_to_close',
        from: 'create_incident',
        fromPort: 'output',
        to: 'close_analysis',
        toPort: 'input',
      },
      {
        id: 'conn_flag_to_close',
        from: 'flag_and_log',
        fromPort: 'output',
        to: 'close_analysis',
        toPort: 'input',
      },
    ],
  },

  {
    id: 'malware_alert_response',
    name: 'Malware Alert Response',
    description: 'Comprehensive malware incident response workflow with host enrichment, risk scoring, isolation, and forensic collection',
    category: 'incident-response',
    severity: 'critical',
    tags: ['malware', 'containment', 'forensics', 'incident-management', 'escalation'],
    icon: '🦠',
    estimatedDuration: '20-30 minutes',
    requiredConnectors: ['siem', 'endpoint-protection', 'incident-management', 'slack'],

    nodes: [
      {
        id: 'siem_alert',
        type: 'siem_alert',
        label: 'SIEM Malware Alert',
        x: 0,
        y: 0,
        config: {
          source: 'siem',
          alertType: 'malware-detected',
          outputVariable: 'siemAlert',
          description: 'SIEM alert for malware detection from EDR/AV tools',
        },
      },
      {
        id: 'enrich_host_data',
        type: 'asset_lookup',
        label: 'Enrich Host Data',
        x: 200,
        y: 0,
        config: {
          inputMapping: {
            hostname: 'siemAlert.hostname',
            ipAddress: 'siemAlert.ipAddress',
          },
          connector: 'siem',
          attributes: ['osVersion', 'criticalSystems', 'networkSegment', 'lastPatchDate', 'users', 'processes'],
          outputVariable: 'hostData',
        },
      },
      {
        id: 'calculate_risk_score',
        type: 'llm_task',
        label: 'Calculate Risk Score',
        x: 400,
        y: 0,
        config: {
          inputMapping: {
            malwareType: 'siemAlert.malwareType',
            severity: 'siemAlert.severity',
            isSystemCritical: 'hostData.criticalSystems',
            networkSegment: 'hostData.networkSegment',
            lastPatchDate: 'hostData.lastPatchDate',
            affectedUsers: 'hostData.users',
          },
          connector: 'openai',
          model: 'gpt-4',
          systemPrompt: 'Calculate risk score for malware incident. Return JSON: {riskScore: 0-100, criticality: "critical"|"high"|"medium"|"low", reasoning: string, recommends_isolation: boolean}',
          outputVariable: 'riskScoring',
          temperature: 0.2,
        },
      },
      {
        id: 'risk_gate',
        type: 'risk_score_gate',
        label: 'Risk Score Gate',
        x: 600,
        y: 0,
        config: {
          inputMapping: {
            riskScore: 'riskScoring.riskScore',
          },
          thresholds: {
            critical: 80,
            high: 60,
            medium: 40,
          },
          outputVariable: 'riskEvaluation',
        },
      },
      {
        id: 'isolate_host',
        type: 'containment_action',
        label: 'Isolate Host',
        x: 800,
        y: 0,
        config: {
          inputMapping: {
            hostname: 'siemAlert.hostname',
            ipAddress: 'siemAlert.ipAddress',
            isolationType: 'network-isolation',
          },
          connector: 'endpoint-protection',
          action: 'isolate-host',
          outputVariable: 'isolationResult',
          notifyUsers: true,
          notificationMessage: 'Your system has been isolated due to security incident. Please contact SOC.',
        },
      },
      {
        id: 'collect_forensics',
        type: 'forensic_snapshot',
        label: 'Collect Forensics',
        x: 1000,
        y: 0,
        config: {
          inputMapping: {
            hostname: 'siemAlert.hostname',
            timestamp: 'siemAlert.timestamp',
          },
          connector: 'endpoint-protection',
          collectItems: ['memory-dump', 'disk-image', 'process-list', 'network-connections', 'file-hashes'],
          outputVariable: 'forensicData',
          storageLocation: 'forensics-vault',
        },
      },
      {
        id: 'create_incident',
        type: 'incident_create',
        label: 'Create Incident',
        x: 1200,
        y: 0,
        config: {
          inputMapping: {
            title: 'Malware Detected on Host',
            hostname: 'siemAlert.hostname',
            severity: 'riskScoring.criticality',
            malwareType: 'siemAlert.malwareType',
            riskScore: 'riskScoring.riskScore',
            forensicsId: 'forensicData.snapshotId',
          },
          incidentType: 'malware',
          outputVariable: 'incidentCreated',
          connector: 'incident-management',
        },
      },
      {
        id: 'escalation_chain',
        type: 'notification_task',
        label: 'Escalation Chain',
        x: 1400,
        y: 0,
        config: {
          inputMapping: {
            incidentId: 'incidentCreated.incidentId',
            severity: 'riskScoring.criticality',
            hostname: 'siemAlert.hostname',
            riskScore: 'riskScoring.riskScore',
          },
          channels: ['slack', 'email', 'pagerduty'],
          escalationChain: ['soc-team', 'incident-commander', 'management'],
          template: 'malware_incident_escalation',
          slackChannel: '#critical-incidents',
          outputVariable: 'escalationResult',
        },
      },
      {
        id: 'generate_post_incident',
        type: 'system_task',
        label: 'Generate Post-Incident Report',
        x: 1600,
        y: 0,
        config: {
          inputMapping: {
            incidentId: 'incidentCreated.incidentId',
            hostname: 'siemAlert.hostname',
            malwareType: 'siemAlert.malwareType',
            timeToDetection: 'siemAlert.timeToDetection',
            timeToIsolation: 'isolationResult.timestamp',
            forensicsId: 'forensicData.snapshotId',
            affectedUsers: 'hostData.users',
          },
          action: 'generate-report',
          template: 'post-incident-report',
          outputVariable: 'postIncidentReport',
          destination: 'incident-vault',
        },
      },
      {
        id: 'close_incident',
        type: 'system_task',
        label: 'Close Incident',
        x: 1800,
        y: 0,
        config: {
          action: 'complete-workflow',
          outputVariable: 'workflowComplete',
        },
      },
    ],

    connections: [
      {
        id: 'conn_siem_to_enrich',
        from: 'siem_alert',
        fromPort: 'output',
        to: 'enrich_host_data',
        toPort: 'input',
      },
      {
        id: 'conn_enrich_to_risk',
        from: 'enrich_host_data',
        fromPort: 'output',
        to: 'calculate_risk_score',
        toPort: 'input',
      },
      {
        id: 'conn_risk_calc_to_gate',
        from: 'calculate_risk_score',
        fromPort: 'output',
        to: 'risk_gate',
        toPort: 'input',
      },
      {
        id: 'conn_gate_to_isolate',
        from: 'risk_gate',
        fromPort: 'output',
        to: 'isolate_host',
        toPort: 'input',
      },
      {
        id: 'conn_isolate_to_forensics',
        from: 'isolate_host',
        fromPort: 'output',
        to: 'collect_forensics',
        toPort: 'input',
      },
      {
        id: 'conn_forensics_to_incident',
        from: 'collect_forensics',
        fromPort: 'output',
        to: 'create_incident',
        toPort: 'input',
      },
      {
        id: 'conn_incident_to_escalation',
        from: 'create_incident',
        fromPort: 'output',
        to: 'escalation_chain',
        toPort: 'input',
      },
      {
        id: 'conn_escalation_to_report',
        from: 'escalation_chain',
        fromPort: 'output',
        to: 'generate_post_incident',
        toPort: 'input',
      },
      {
        id: 'conn_report_to_close',
        from: 'generate_post_incident',
        fromPort: 'output',
        to: 'close_incident',
        toPort: 'input',
      },
    ],
  },

  {
    id: 'privilege_escalation_detection',
    name: 'Privilege Escalation Detection & Response',
    description: 'Detects anomalous privilege escalation, enforces dual approval for remediation, and maintains audit logs',
    category: 'incident-response',
    severity: 'high',
    tags: ['privilege-escalation', 'anomaly-detection', 'dual-approval', 'remediation', 'audit'],
    icon: '⬆️',
    estimatedDuration: '15-25 minutes',
    requiredConnectors: ['identity-provider', 'siem', 'slack', 'jira'],

    nodes: [
      {
        id: 'pe_alert_trigger',
        type: 'siem_alert',
        label: 'Privilege Escalation Alert',
        x: 0,
        y: 0,
        config: {
          source: 'siem',
          alertType: 'privilege-escalation',
          outputVariable: 'peAlert',
          description: 'Alert triggered on unexpected privilege escalation events',
        },
      },
      {
        id: 'lookup_user_perms',
        type: 'user_lookup',
        label: 'Lookup User Permissions',
        x: 200,
        y: 0,
        config: {
          inputMapping: {
            userId: 'peAlert.userId',
          },
          connector: 'identity-provider',
          attributes: ['roles', 'groups', 'permissions', 'assignmentDate', 'approver'],
          outputVariable: 'userPermissions',
        },
      },
      {
        id: 'compare_baseline',
        type: 'threat_intel_lookup',
        label: 'Compare Baseline',
        x: 400,
        y: 0,
        config: {
          inputMapping: {
            userId: 'peAlert.userId',
            currentRoles: 'userPermissions.roles',
            currentPermissions: 'userPermissions.permissions',
          },
          lookupType: 'baseline-comparison',
          connector: 'siem',
          outputVariable: 'baselineComparison',
        },
      },
      {
        id: 'is_anomalous',
        type: 'condition',
        label: 'Is Anomalous?',
        x: 600,
        y: 0,
        config: {
          inputMapping: {
            deviationScore: 'baselineComparison.deviationScore',
            isUnexpected: 'baselineComparison.isUnexpected',
          },
          conditions: [
            {
              id: 'cond_anomaly',
              expression: 'deviationScore > 0.75 || isUnexpected',
              label: 'Anomalous',
              outputVariable: 'isAnomalous',
            },
          ],
          defaultOutput: 'authorized',
        },
      },
      {
        id: 'dual_approval',
        type: 'dual_approval',
        label: 'Dual Approval Gate',
        x: 800,
        y: 0,
        config: {
          inputMapping: {
            userId: 'peAlert.userId',
            escalatedRoles: 'peAlert.escalatedRoles',
            timestamp: 'peAlert.timestamp',
          },
          approvalGroups: [
            { role: 'security-lead', count: 1 },
            { role: 'manager', count: 1 },
          ],
          timeout: 3600,
          outputVariable: 'dualApprovalResult',
          escalateIfNoResponse: true,
        },
      },
      {
        id: 'approval_decision',
        type: 'switch_case',
        label: 'Approval Decision',
        x: 1000,
        y: 0,
        config: {
          inputMapping: {
            decision: 'dualApprovalResult.decision',
          },
          cases: [
            {
              id: 'case_approved',
              value: 'approved',
              label: 'Approved - Keep Permissions',
              outputVariable: 'approvedDecision',
            },
            {
              id: 'case_rejected',
              value: 'rejected',
              label: 'Rejected - Revoke',
              outputVariable: 'rejectedDecision',
            },
          ],
          defaultCase: 'rejected',
        },
      },
      {
        id: 'revoke_permissions',
        type: 'user_action',
        label: 'Revoke Excess Permissions',
        x: 1200,
        y: -100,
        config: {
          inputMapping: {
            userId: 'peAlert.userId',
            permissionsToRevoke: 'peAlert.escalatedRoles',
            reason: 'Unauthorized privilege escalation detected',
          },
          action: 'revoke-permissions',
          connector: 'identity-provider',
          outputVariable: 'revocationResult',
        },
      },
      {
        id: 'keep_permissions',
        type: 'system_task',
        label: 'Keep Permissions (Approved)',
        x: 1200,
        y: 100,
        config: {
          inputMapping: {
            userId: 'peAlert.userId',
            reason: 'Approved by dual approval gate',
          },
          action: 'log-approval',
          destination: 'audit-log',
          outputVariable: 'approvalLog',
        },
      },
      {
        id: 'audit_log_entry',
        type: 'system_task',
        label: 'Create Audit Log Entry',
        x: 1400,
        y: 0,
        config: {
          inputMapping: {
            userId: 'peAlert.userId',
            action: 'revoke_permissions.action || keep_permissions.action',
            timestamp: 'peAlert.timestamp',
            reason: 'dualApprovalResult.reason',
            approvers: 'dualApprovalResult.approvers',
          },
          action: 'create-audit-entry',
          destination: 'audit-log',
          outputVariable: 'auditEntry',
        },
      },
      {
        id: 'notify_result',
        type: 'notification_task',
        label: 'Notify Stakeholders',
        x: 1600,
        y: 0,
        config: {
          inputMapping: {
            userId: 'peAlert.userId',
            decision: 'approval_decision.decision',
            revokedRoles: 'revoke_permissions.revokedRoles || null',
          },
          channels: ['slack', 'email'],
          template: 'privilege_escalation_decision',
          recipients: ['security-team@company.com', 'peAlert.userId'],
          slackChannel: '#security-operations',
          outputVariable: 'notification',
        },
      },
      {
        id: 'close_detection',
        type: 'system_task',
        label: 'Close Detection',
        x: 1800,
        y: 0,
        config: {
          action: 'complete-workflow',
          outputVariable: 'workflowComplete',
        },
      },
    ],

    connections: [
      {
        id: 'conn_alert_to_lookup',
        from: 'pe_alert_trigger',
        fromPort: 'output',
        to: 'lookup_user_perms',
        toPort: 'input',
      },
      {
        id: 'conn_lookup_to_compare',
        from: 'lookup_user_perms',
        fromPort: 'output',
        to: 'compare_baseline',
        toPort: 'input',
      },
      {
        id: 'conn_compare_to_condition',
        from: 'compare_baseline',
        fromPort: 'output',
        to: 'is_anomalous',
        toPort: 'input',
      },
      {
        id: 'conn_anomalous_yes',
        from: 'is_anomalous',
        fromPort: 'anomalous',
        to: 'dual_approval',
        toPort: 'input',
      },
      {
        id: 'conn_anomalous_no',
        from: 'is_anomalous',
        fromPort: 'authorized',
        to: 'audit_log_entry',
        toPort: 'input',
      },
      {
        id: 'conn_approval_to_decision',
        from: 'dual_approval',
        fromPort: 'output',
        to: 'approval_decision',
        toPort: 'input',
      },
      {
        id: 'conn_decision_revoke',
        from: 'approval_decision',
        fromPort: 'caseRejected',
        to: 'revoke_permissions',
        toPort: 'input',
      },
      {
        id: 'conn_decision_keep',
        from: 'approval_decision',
        fromPort: 'caseApproved',
        to: 'keep_permissions',
        toPort: 'input',
      },
      {
        id: 'conn_revoke_to_audit',
        from: 'revoke_permissions',
        fromPort: 'output',
        to: 'audit_log_entry',
        toPort: 'input',
      },
      {
        id: 'conn_keep_to_audit',
        from: 'keep_permissions',
        fromPort: 'output',
        to: 'audit_log_entry',
        toPort: 'input',
      },
      {
        id: 'conn_audit_to_notify',
        from: 'audit_log_entry',
        fromPort: 'output',
        to: 'notify_result',
        toPort: 'input',
      },
      {
        id: 'conn_notify_to_close',
        from: 'notify_result',
        fromPort: 'output',
        to: 'close_detection',
        toPort: 'input',
      },
    ],
  },

  // ============================================================================
  // COMPLIANCE & AUDIT PLAYBOOKS
  // ============================================================================

  {
    id: 'compliance_evidence_collection',
    name: 'Compliance Evidence Collection',
    description: 'Monthly automated collection of compliance evidence including access logs, configuration snapshots, and policy documentation',
    category: 'compliance-audit',
    severity: 'low',
    tags: ['compliance', 'audit', 'evidence-collection', 'scheduled', 'parallel'],
    icon: '📋',
    estimatedDuration: '30-45 minutes',
    requiredConnectors: ['identity-provider', 'siem', 'github', 'aws'],

    nodes: [
      {
        id: 'schedule_monthly',
        type: 'schedule',
        label: 'Monthly Schedule',
        x: 0,
        y: 0,
        config: {
          cronExpression: '0 2 1 * *',
          timezone: 'UTC',
          description: 'Runs on the 1st of each month at 02:00 UTC',
          outputVariable: 'scheduleTriggered',
        },
      },
      {
        id: 'parallel_collection',
        type: 'parallel_fork',
        label: 'Parallel Evidence Collection',
        x: 200,
        y: 0,
        config: {
          branches: 3,
          outputVariable: 'collectionStart',
        },
      },
      {
        id: 'collect_access_logs',
        type: 'evidence_collect',
        label: 'Collect Access Logs',
        x: 400,
        y: -150,
        config: {
          inputMapping: {
            startTime: 'schedule_monthly.lastRunTime || 30 days ago',
            endTime: 'schedule_monthly.currentTime',
          },
          connector: 'siem',
          collectType: 'access-logs',
          filters: {
            eventTypes: ['login', 'access-granted', 'access-denied', 'privilege-change'],
          },
          outputVariable: 'accessLogs',
          compressionFormat: 'gzip',
          destination: 'evidence-vault',
        },
      },
      {
        id: 'collect_config_snapshots',
        type: 'evidence_collect',
        label: 'Collect Config Snapshots',
        x: 400,
        y: 0,
        config: {
          inputMapping: {
            timestamp: 'schedule_monthly.currentTime',
          },
          connector: 'aws',
          collectType: 'configuration-snapshot',
          resources: [
            'security-groups',
            'iam-policies',
            'kms-keys',
            'cloudtrail-config',
            's3-bucket-policies',
          ],
          outputVariable: 'configSnapshots',
          destination: 'evidence-vault',
        },
      },
      {
        id: 'collect_policy_docs',
        type: 'evidence_collect',
        label: 'Collect Policy Documentation',
        x: 400,
        y: 150,
        config: {
          inputMapping: {
            timestamp: 'schedule_monthly.currentTime',
          },
          connector: 'github',
          collectType: 'policy-documents',
          paths: ['/policies', '/security', '/compliance'],
          outputVariable: 'policyDocs',
          includeCommitHistory: true,
          destination: 'evidence-vault',
        },
      },
      {
        id: 'join_collection',
        type: 'parallel_join',
        label: 'Join All Evidence',
        x: 600,
        y: 0,
        config: {
          expectedBranches: 3,
          waitForAll: true,
          outputVariable: 'allEvidence',
        },
      },
      {
        id: 'aggregate_evidence',
        type: 'system_task',
        label: 'Aggregate Evidence',
        x: 800,
        y: 0,
        config: {
          inputMapping: {
            accessLogsId: 'collect_access_logs.evidenceId',
            configSnapshotsId: 'collect_config_snapshots.evidenceId',
            policyDocsId: 'collect_policy_docs.evidenceId',
            collectionTime: 'schedule_monthly.currentTime',
          },
          action: 'aggregate-evidence',
          outputVariable: 'aggregatedEvidence',
          destination: 'evidence-vault',
        },
      },
      {
        id: 'generate_compliance_report',
        type: 'system_task',
        label: 'Generate Compliance Report',
        x: 1000,
        y: 0,
        config: {
          inputMapping: {
            evidencePackageId: 'aggregatedEvidence.packageId',
            accessLogCount: 'collect_access_logs.recordCount',
            configItems: 'collect_config_snapshots.itemCount',
            policyDocuments: 'collect_policy_docs.documentCount',
            reportMonth: 'schedule_monthly.month',
          },
          action: 'generate-report',
          template: 'compliance-evidence-report',
          outputVariable: 'complianceReport',
          destination: 'compliance-vault',
        },
      },
      {
        id: 'store_vault',
        type: 'system_task',
        label: 'Store in Evidence Vault',
        x: 1200,
        y: 0,
        config: {
          inputMapping: {
            reportId: 'complianceReport.reportId',
            packageId: 'aggregatedEvidence.packageId',
            retentionDays: 2555,
          },
          action: 'store-in-vault',
          vaultType: 'evidence-vault',
          outputVariable: 'vaultStorage',
        },
      },
      {
        id: 'notify_compliance',
        type: 'notification_task',
        label: 'Notify Compliance Team',
        x: 1400,
        y: 0,
        config: {
          inputMapping: {
            reportId: 'complianceReport.reportId',
            packageId: 'aggregatedEvidence.packageId',
            recordCount: 'collect_access_logs.recordCount',
            reportMonth: 'schedule_monthly.month',
          },
          channels: ['email', 'slack'],
          template: 'compliance_evidence_collected',
          recipients: 'compliance-team@company.com',
          slackChannel: '#compliance-notifications',
          outputVariable: 'notification',
        },
      },
      {
        id: 'close_collection',
        type: 'system_task',
        label: 'Close Collection',
        x: 1600,
        y: 0,
        config: {
          action: 'complete-workflow',
          outputVariable: 'workflowComplete',
        },
      },
    ],

    connections: [
      {
        id: 'conn_schedule_to_fork',
        from: 'schedule_monthly',
        fromPort: 'output',
        to: 'parallel_collection',
        toPort: 'input',
      },
      {
        id: 'conn_fork_to_access',
        from: 'parallel_collection',
        fromPort: 'branch0',
        to: 'collect_access_logs',
        toPort: 'input',
      },
      {
        id: 'conn_fork_to_config',
        from: 'parallel_collection',
        fromPort: 'branch1',
        to: 'collect_config_snapshots',
        toPort: 'input',
      },
      {
        id: 'conn_fork_to_policy',
        from: 'parallel_collection',
        fromPort: 'branch2',
        to: 'collect_policy_docs',
        toPort: 'input',
      },
      {
        id: 'conn_access_to_join',
        from: 'collect_access_logs',
        fromPort: 'output',
        to: 'join_collection',
        toPort: 'input0',
      },
      {
        id: 'conn_config_to_join',
        from: 'collect_config_snapshots',
        fromPort: 'output',
        to: 'join_collection',
        toPort: 'input1',
      },
      {
        id: 'conn_policy_to_join',
        from: 'collect_policy_docs',
        fromPort: 'output',
        to: 'join_collection',
        toPort: 'input2',
      },
      {
        id: 'conn_join_to_aggregate',
        from: 'join_collection',
        fromPort: 'output',
        to: 'aggregate_evidence',
        toPort: 'input',
      },
      {
        id: 'conn_aggregate_to_report',
        from: 'aggregate_evidence',
        fromPort: 'output',
        to: 'generate_compliance_report',
        toPort: 'input',
      },
      {
        id: 'conn_report_to_vault',
        from: 'generate_compliance_report',
        fromPort: 'output',
        to: 'store_vault',
        toPort: 'input',
      },
      {
        id: 'conn_vault_to_notify',
        from: 'store_vault',
        fromPort: 'output',
        to: 'notify_compliance',
        toPort: 'input',
      },
      {
        id: 'conn_notify_to_close',
        from: 'notify_compliance',
        fromPort: 'output',
        to: 'close_collection',
        toPort: 'input',
      },
    ],
  },

  {
    id: 'policy_violation_remediation',
    name: 'Policy Violation Remediation',
    description: 'Automated policy violation detection with risk-based remediation: auto-fix low-risk violations, approval gate for high-risk',
    category: 'compliance-audit',
    severity: 'medium',
    tags: ['policy-violation', 'auto-remediation', 'approval-gate', 'compliance'],
    icon: '🔧',
    estimatedDuration: '10-20 minutes',
    requiredConnectors: ['compliance-scanner', 'aws', 'slack', 'jira'],

    nodes: [
      {
        id: 'violation_alert',
        type: 'webhook',
        label: 'Policy Violation Alert',
        x: 0,
        y: 0,
        config: {
          webhookPath: '/webhooks/policy-violation',
          method: 'POST',
          outputVariable: 'violationAlert',
          description: 'Triggered by compliance scanner on policy violation detection',
        },
      },
      {
        id: 'identify_resource',
        type: 'asset_lookup',
        label: 'Identify Violating Resource',
        x: 200,
        y: 0,
        config: {
          inputMapping: {
            resourceId: 'violationAlert.resourceId',
            resourceType: 'violationAlert.resourceType',
          },
          connector: 'aws',
          attributes: ['status', 'owner', 'tags', 'createdDate', 'lastModified', 'criticality'],
          outputVariable: 'resourceDetails',
        },
      },
      {
        id: 'assess_risk',
        type: 'llm_task',
        label: 'Assess Remediation Risk',
        x: 400,
        y: 0,
        config: {
          inputMapping: {
            violationPolicy: 'violationAlert.policy',
            violationSeverity: 'violationAlert.severity',
            resourceCritical: 'resourceDetails.criticality',
            affectedServices: 'resourceDetails.dependentServices || 0',
            owner: 'resourceDetails.owner',
          },
          connector: 'openai',
          model: 'gpt-4',
          systemPrompt: 'Assess if policy violation can be auto-remediated safely. Return JSON: {riskLevel: "low"|"medium"|"high"|"critical", autoRemediateOk: boolean, reasoning: string, recommendedFix: string}',
          outputVariable: 'riskAssessment',
          temperature: 0.2,
        },
      },
      {
        id: 'risk_branch',
        type: 'switch_case',
        label: 'Risk Level Branch',
        x: 600,
        y: 0,
        config: {
          inputMapping: {
            riskLevel: 'riskAssessment.riskLevel',
            autoRemediateOk: 'riskAssessment.autoRemediateOk',
          },
          cases: [
            {
              id: 'case_low_risk',
              value: 'low',
              label: 'Low Risk - Auto Remediate',
              outputVariable: 'lowRisk',
            },
            {
              id: 'case_high_risk',
              value: 'high|critical',
              label: 'High Risk - Approval Required',
              outputVariable: 'highRisk',
            },
          ],
          defaultCase: 'highRisk',
        },
      },
      {
        id: 'auto_remediate',
        type: 'system_task',
        label: 'Auto-Remediate Violation',
        x: 800,
        y: -100,
        config: {
          inputMapping: {
            resourceId: 'resourceDetails.resourceId',
            remediation: 'riskAssessment.recommendedFix',
            policy: 'violationAlert.policy',
          },
          connector: 'aws',
          action: 'apply-remediation',
          outputVariable: 'remediationResult',
        },
      },
      {
        id: 'approval_gate_high_risk',
        type: 'approval_gate',
        label: 'Approval Gate',
        x: 800,
        y: 100,
        config: {
          inputMapping: {
            resourceId: 'resourceDetails.resourceId',
            policy: 'violationAlert.policy',
            violation: 'violationAlert.description',
            recommendedFix: 'riskAssessment.recommendedFix',
          },
          approvalGroups: ['compliance-team', 'resource-owner'],
          timeout: 86400,
          outputVariable: 'approvalDecision',
          escalateIfNoResponse: true,
        },
      },
      {
        id: 'approval_outcome',
        type: 'switch_case',
        label: 'Approval Outcome',
        x: 1000,
        y: 100,
        config: {
          inputMapping: {
            decision: 'approvalDecision.decision',
          },
          cases: [
            {
              id: 'case_approved',
              value: 'approved',
              label: 'Approved',
              outputVariable: 'approved',
            },
            {
              id: 'case_rejected',
              value: 'rejected',
              label: 'Rejected',
              outputVariable: 'rejected',
            },
          ],
          defaultCase: 'rejected',
        },
      },
      {
        id: 'apply_remediation',
        type: 'system_task',
        label: 'Apply Approved Fix',
        x: 1200,
        y: 0,
        config: {
          inputMapping: {
            resourceId: 'resourceDetails.resourceId',
            remediation: 'riskAssessment.recommendedFix',
          },
          connector: 'aws',
          action: 'apply-remediation',
          outputVariable: 'remediationResult',
        },
      },
      {
        id: 'verify_remediation',
        type: 'system_task',
        label: 'Verify Remediation',
        x: 1400,
        y: 0,
        config: {
          inputMapping: {
            resourceId: 'resourceDetails.resourceId',
            policy: 'violationAlert.policy',
          },
          connector: 'compliance-scanner',
          action: 'verify-fix',
          outputVariable: 'verificationResult',
        },
      },
      {
        id: 'verification_gate',
        type: 'condition',
        label: 'Verification Passed?',
        x: 1600,
        y: 0,
        config: {
          inputMapping: {
            isCompliant: 'verificationResult.isCompliant',
          },
          conditions: [
            {
              id: 'cond_verified',
              expression: 'isCompliant',
              label: 'Compliant',
              outputVariable: 'isCompliant',
            },
          ],
          defaultOutput: 'stillViolated',
        },
      },
      {
        id: 'close_ticket',
        type: 'system_task',
        label: 'Close Ticket',
        x: 1800,
        y: -100,
        config: {
          inputMapping: {
            resourceId: 'resourceDetails.resourceId',
          },
          action: 'close-ticket',
          destination: 'jira',
          outputVariable: 'ticketClosed',
        },
      },
      {
        id: 'escalate_unresolved',
        type: 'notification_task',
        label: 'Escalate Unresolved',
        x: 1800,
        y: 100,
        config: {
          inputMapping: {
            resourceId: 'resourceDetails.resourceId',
            violation: 'violationAlert.description',
          },
          channels: ['slack', 'email'],
          template: 'policy_violation_escalation',
          recipients: 'compliance-team@company.com',
          slackChannel: '#compliance-violations',
          outputVariable: 'escalation',
        },
      },
      {
        id: 'complete_remediation',
        type: 'system_task',
        label: 'Complete',
        x: 2000,
        y: 0,
        config: {
          action: 'complete-workflow',
          outputVariable: 'workflowComplete',
        },
      },
    ],

    connections: [
      {
        id: 'conn_alert_to_identify',
        from: 'violation_alert',
        fromPort: 'output',
        to: 'identify_resource',
        toPort: 'input',
      },
      {
        id: 'conn_identify_to_assess',
        from: 'identify_resource',
        fromPort: 'output',
        to: 'assess_risk',
        toPort: 'input',
      },
      {
        id: 'conn_assess_to_branch',
        from: 'assess_risk',
        fromPort: 'output',
        to: 'risk_branch',
        toPort: 'input',
      },
      {
        id: 'conn_low_risk',
        from: 'risk_branch',
        fromPort: 'caseLowRisk',
        to: 'auto_remediate',
        toPort: 'input',
      },
      {
        id: 'conn_high_risk',
        from: 'risk_branch',
        fromPort: 'caseHighRisk',
        to: 'approval_gate_high_risk',
        toPort: 'input',
      },
      {
        id: 'conn_approval_to_outcome',
        from: 'approval_gate_high_risk',
        fromPort: 'output',
        to: 'approval_outcome',
        toPort: 'input',
      },
      {
        id: 'conn_approved',
        from: 'approval_outcome',
        fromPort: 'caseApproved',
        to: 'apply_remediation',
        toPort: 'input',
      },
      {
        id: 'conn_auto_to_verify',
        from: 'auto_remediate',
        fromPort: 'output',
        to: 'verify_remediation',
        toPort: 'input',
      },
      {
        id: 'conn_apply_to_verify',
        from: 'apply_remediation',
        fromPort: 'output',
        to: 'verify_remediation',
        toPort: 'input',
      },
      {
        id: 'conn_verify_to_gate',
        from: 'verify_remediation',
        fromPort: 'output',
        to: 'verification_gate',
        toPort: 'input',
      },
      {
        id: 'conn_compliant',
        from: 'verification_gate',
        fromPort: 'compliant',
        to: 'close_ticket',
        toPort: 'input',
      },
      {
        id: 'conn_violated',
        from: 'verification_gate',
        fromPort: 'stillViolated',
        to: 'escalate_unresolved',
        toPort: 'input',
      },
      {
        id: 'conn_close_to_complete',
        from: 'close_ticket',
        fromPort: 'output',
        to: 'complete_remediation',
        toPort: 'input',
      },
      {
        id: 'conn_escalate_to_complete',
        from: 'escalate_unresolved',
        fromPort: 'output',
        to: 'complete_remediation',
        toPort: 'input',
      },
      {
        id: 'conn_rejected',
        from: 'approval_outcome',
        fromPort: 'caseRejected',
        to: 'escalate_unresolved',
        toPort: 'input',
      },
    ],
  },

  // ============================================================================
  // PLATFORM OPERATIONS PLAYBOOKS
  // ============================================================================

  {
    id: 'user_invitation_email',
    name: 'User Invitation Email',
    description: 'System workflow that sends a professional HTML invitation email to new users via the Email SMTP connector. Auto-triggered by the Invite User flow in Team Management.',
    category: 'platform-ops',
    severity: 'low',
    tags: ['user-management', 'email', 'onboarding', 'invitation', 'system-workflow'],
    icon: '✉',
    estimatedDuration: '< 1 minute',
    requiredConnectors: ['email-smtp-v1'],

    nodes: [
      {
        id: 'trigger_invite',
        type: 'manual_trigger',
        label: 'Invitation Triggered',
        x: 80,
        y: 200,
        config: {
          description: 'Triggered automatically when a user invitation is sent from Team Management. Receives invitee details and registration URL as workflow variables.',
        },
      },
      {
        id: 'send_invite_email',
        type: 'notification_task',
        label: 'Send Invitation Email',
        x: 400,
        y: 200,
        config: {
          channel: 'email',
          recipients: '{{invitee_email}}',
          template: 'custom',
          subject: "You've Been Invited to Join Prove7 Platform",
          body: '<div style="font-family:Inter,system-ui,sans-serif;max-width:600px;margin:0 auto;background:#fff;border:1px solid #e7e5e4;border-radius:12px;overflow:hidden"><div style="background:linear-gradient(135deg,#0a1d44 0%,#0e3552 100%);padding:32px;text-align:center"><h1 style="color:#fff;font-size:22px;margin:0">Welcome to Prove7</h1><p style="color:rgba(255,255,255,0.85);font-size:13px;margin:6px 0 0">Trusted Autonomy Platform</p></div><div style="padding:32px"><p style="font-size:15px;color:#18181b;margin:0 0 20px">Hi <strong>{{invitee_name}}</strong>,</p><p style="font-size:14px;color:#52525b;line-height:1.6;margin:0 0 20px">You have been invited to join the <strong>Prove7 Platform</strong> as <strong>{{invitee_role}}</strong>. Click the button below to complete your registration and set your password.</p><div style="text-align:center;margin:28px 0"><a href="{{registration_url}}" style="display:inline-block;padding:14px 36px;background:#0e3552;color:#fff;font-size:14px;font-weight:600;text-decoration:none;border-radius:8px">Complete Registration</a></div><div style="background:#f5f5f4;border-radius:8px;padding:16px;margin:20px 0"><div style="font-size:12px;color:#71717a;margin-bottom:8px"><strong>Invitation Details:</strong></div><div style="font-size:13px;color:#18181b">Email: <strong>{{invitee_email}}</strong></div><div style="font-size:13px;color:#18181b">Role: <strong>{{invitee_role}}</strong></div><div style="font-size:13px;color:#18181b">Organization: <strong>{{org_name}}</strong></div></div><div style="border-top:1px solid #e7e5e4;padding-top:16px;margin-top:24px"><p style="font-size:11px;color:#a8a29e;margin:0">This invitation link expires in <strong>24 hours</strong>. If you did not expect this invitation, you can safely ignore this email.</p><p style="font-size:11px;color:#a8a29e;margin:8px 0 0">If the button does not work, copy and paste this URL into your browser:</p><p style="font-size:10px;color:#78716c;word-break:break-all;margin:4px 0 0">{{registration_url}}</p></div></div></div>',
          priority: 'high',
          includeAttachments: false,
        },
      },
    ],

    connections: [
      {
        id: 'conn_trigger_to_email',
        from: 'trigger_invite',
        fromPort: 'default',
        to: 'send_invite_email',
        toPort: 'default',
      },
    ],
  },

  // ============================================================================
  // DATA PROTECTION PLAYBOOKS
  // ============================================================================

  {
    id: 'customer_lookup_pii_protected',
    name: 'Customer Data Lookup — PII Protected',
    description:
      'Looks up customer records via a CRM connector, scans the response for PII (SSN, email, phone), ' +
      'tokenizes any detected PII through the Vault, and logs the access in the audit trail. ' +
      'Demonstrates inline enforcement nodes in a user-built workflow.',
    category: 'data-protection',
    severity: 'medium',
    tags: ['pii', 'crm', 'data-protection', 'tokenization', 'audit', 'customer'],
    icon: '🔒',
    estimatedDuration: '2-5 seconds',
    requiredConnectors: ['crm'],

    nodes: [
      {
        id: 'trigger_lookup',
        type: 'manual_start',
        label: 'Customer Lookup Request',
        x: 0,
        y: 0,
        config: {
          outputVariable: 'lookupRequest',
          description: 'Receives a customer lookup request with customerId and reason',
        },
      },
      {
        id: 'policy_check',
        type: 'policy_rule_evaluation',
        label: 'L1: Access Policy Check',
        x: 0,
        y: 150,
        config: {
          enforcementPoint: 'WORKFLOW_ENTRY',
        },
      },
      {
        id: 'crm_lookup',
        type: 'connector_task',
        label: 'CRM: Fetch Customer Record',
        x: 0,
        y: 300,
        config: {
          connectorType: 'crm',
          action: 'get-record',
          outputVariable: 'customerRecord',
        },
      },
      {
        id: 'dpg_scan_response',
        type: 'dpg_scan',
        label: 'DPG: Scan Response for PII',
        x: 0,
        y: 450,
        config: {
          connectorType: 'crm',
        },
      },
      {
        id: 'vault_tokenize',
        type: 'token_vault',
        label: 'Vault: Tokenize Detected PII',
        x: 0,
        y: 600,
        config: {
          action: 'tokenize',
          category: 'PII',
        },
      },
      {
        id: 'audit_access',
        type: 'audit_event',
        label: 'Audit: Record Data Access',
        x: 0,
        y: 750,
        config: {
          eventType: 'DATA_CLASSIFICATION',
          decision: 'ALLOW',
        },
      },
    ],

    connections: [
      {
        id: 'conn_trigger_to_policy',
        from: 'trigger_lookup',
        fromPort: 'default',
        to: 'policy_check',
        toPort: 'default',
      },
      {
        id: 'conn_policy_to_crm',
        from: 'policy_check',
        fromPort: 'default',
        to: 'crm_lookup',
        toPort: 'default',
      },
      {
        id: 'conn_crm_to_dpg',
        from: 'crm_lookup',
        fromPort: 'default',
        to: 'dpg_scan_response',
        toPort: 'default',
      },
      {
        id: 'conn_dpg_to_vault',
        from: 'dpg_scan_response',
        fromPort: 'default',
        to: 'vault_tokenize',
        toPort: 'default',
      },
      {
        id: 'conn_vault_to_audit',
        from: 'vault_tokenize',
        fromPort: 'default',
        to: 'audit_access',
        toPort: 'default',
      },
    ],
  },

  {
    id: 'llm_inference_policy_enforced',
    name: 'LLM Inference — Full Policy Enforcement',
    description:
      'Runs an LLM inference call wrapped with full policy enforcement: entry gate validates rules, ' +
      'DPG scans the prompt outbound, DPG scans the response inbound, and the audit trail records the call. ' +
      'Use this pattern whenever an agent workflow calls an LLM with user-provided data.',
    category: 'data-protection',
    severity: 'high',
    tags: ['llm', 'inference', 'policy', 'dpg', 'audit', 'enforcement', 'prompt'],
    icon: '🤖',
    estimatedDuration: '3-8 seconds',
    requiredConnectors: [],

    nodes: [
      {
        id: 'trigger_inference',
        type: 'manual_start',
        label: 'Inference Request',
        x: 0,
        y: 0,
        config: {
          outputVariable: 'inferenceRequest',
          description: 'Receives inference request with prompt, agentId, userId, context',
        },
      },
      {
        id: 'entry_gate',
        type: 'policy_enforcement_gate',
        label: 'Full Enforcement Gate (6-Stage)',
        x: 0,
        y: 150,
        config: {},
      },
      {
        id: 'dpg_outbound',
        type: 'dpg_scan_outbound',
        label: 'DPG: Scan Prompt (Outbound)',
        x: 0,
        y: 300,
        config: {
          connectorType: 'llm_inference',
        },
      },
      {
        id: 'llm_call',
        type: 'llm_task',
        label: 'LLM Inference Call',
        x: 0,
        y: 450,
        config: {
          model: 'default',
          systemPrompt: 'You are a helpful assistant. Follow all safety guidelines.',
          maxTokens: 2000,
          temperature: 0.7,
        },
      },
      {
        id: 'dpg_inbound',
        type: 'dpg_scan',
        label: 'DPG: Scan Response (Inbound)',
        x: 0,
        y: 600,
        config: {
          connectorType: 'llm_inference',
        },
      },
      {
        id: 'audit_inference',
        type: 'audit_event',
        label: 'Audit: Record Inference Call',
        x: 0,
        y: 750,
        config: {
          eventType: 'INFERENCE_CALL',
          decision: 'ALLOW',
        },
      },
    ],

    connections: [
      {
        id: 'conn_trigger_to_gate',
        from: 'trigger_inference',
        fromPort: 'default',
        to: 'entry_gate',
        toPort: 'default',
      },
      {
        id: 'conn_gate_to_dpg_out',
        from: 'entry_gate',
        fromPort: 'default',
        to: 'dpg_outbound',
        toPort: 'default',
      },
      {
        id: 'conn_dpg_out_to_llm',
        from: 'dpg_outbound',
        fromPort: 'default',
        to: 'llm_call',
        toPort: 'default',
      },
      {
        id: 'conn_llm_to_dpg_in',
        from: 'llm_call',
        fromPort: 'default',
        to: 'dpg_inbound',
        toPort: 'default',
      },
      {
        id: 'conn_dpg_in_to_audit',
        from: 'dpg_inbound',
        fromPort: 'default',
        to: 'audit_inference',
        toPort: 'default',
      },
    ],
  },

  {
    id: 'connector_guard_pattern',
    name: 'External API Call — Connector Guard Pattern',
    description:
      'Wraps any external connector call (Slack, ServiceNow, Salesforce, etc.) with the Connector Guard pattern: ' +
      'policy rules validate access → DPG scans outbound payload for sensitive data → connector executes → ' +
      'DPG scans inbound response → compliance score updated → audit trail. ' +
      'Copy this pattern for any agent workflow that calls external APIs.',
    category: 'data-protection',
    severity: 'high',
    tags: ['connector', 'guard', 'api', 'dpg', 'outbound', 'inbound', 'compliance'],
    icon: '🛡',
    estimatedDuration: '3-10 seconds',
    requiredConnectors: [],

    nodes: [
      {
        id: 'trigger_api_call',
        type: 'manual_start',
        label: 'API Call Request',
        x: 0,
        y: 0,
        config: {
          outputVariable: 'apiRequest',
          description: 'Receives external API call request with connectorId, action, and payload',
        },
      },
      {
        id: 'policy_rules',
        type: 'policy_rule_evaluation',
        label: 'L1: Connector Access Rules',
        x: 0,
        y: 150,
        config: {
          enforcementPoint: 'CONNECTOR_GUARD',
        },
      },
      {
        id: 'dpg_outbound',
        type: 'dpg_scan_outbound',
        label: 'DPG: Scan Outbound Payload',
        x: 0,
        y: 300,
        config: {},
      },
      {
        id: 'connector_call',
        type: 'connector_task',
        label: 'Execute Connector Action',
        x: 0,
        y: 450,
        config: {
          outputVariable: 'apiResponse',
        },
      },
      {
        id: 'dpg_inbound',
        type: 'dpg_scan',
        label: 'DPG: Scan Inbound Response',
        x: 0,
        y: 600,
        config: {},
      },
      {
        id: 'compliance_score',
        type: 'compliance_scoring',
        label: 'Update Compliance Score',
        x: 0,
        y: 750,
        config: {},
      },
      {
        id: 'audit_api_call',
        type: 'audit_event',
        label: 'Audit: Record API Access',
        x: 0,
        y: 900,
        config: {
          eventType: 'CONNECTOR_GUARD',
          decision: 'ALLOW',
        },
      },
    ],

    connections: [
      {
        id: 'conn_trigger_to_rules',
        from: 'trigger_api_call',
        fromPort: 'default',
        to: 'policy_rules',
        toPort: 'default',
      },
      {
        id: 'conn_rules_to_dpg_out',
        from: 'policy_rules',
        fromPort: 'default',
        to: 'dpg_outbound',
        toPort: 'default',
      },
      {
        id: 'conn_dpg_out_to_connector',
        from: 'dpg_outbound',
        fromPort: 'default',
        to: 'connector_call',
        toPort: 'default',
      },
      {
        id: 'conn_connector_to_dpg_in',
        from: 'connector_call',
        fromPort: 'default',
        to: 'dpg_inbound',
        toPort: 'default',
      },
      {
        id: 'conn_dpg_in_to_compliance',
        from: 'dpg_inbound',
        fromPort: 'default',
        to: 'compliance_score',
        toPort: 'default',
      },
      {
        id: 'conn_compliance_to_audit',
        from: 'compliance_score',
        fromPort: 'default',
        to: 'audit_api_call',
        toPort: 'default',
      },
    ],
  },
];

/**
 * Helper function to get a playbook by ID
 */
export const getPlaybookById = (playbookId) => {
  return SOAR_PLAYBOOKS.find((p) => p.id === playbookId);
};

/**
 * Helper function to get playbooks by category
 */
export const getPlaybooksByCategory = (categoryId) => {
  return SOAR_PLAYBOOKS.filter((p) => p.category === categoryId);
};

/**
 * Helper function to search playbooks
 */
export const searchPlaybooks = (query) => {
  const lowerQuery = query.toLowerCase();
  return SOAR_PLAYBOOKS.filter(
    (p) =>
      p.name.toLowerCase().includes(lowerQuery) ||
      p.description.toLowerCase().includes(lowerQuery) ||
      p.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  );
};

/**
 * Helper function to get required connectors for a playbook
 */
export const getRequiredConnectors = (playbookId) => {
  const playbook = getPlaybookById(playbookId);
  return playbook ? playbook.requiredConnectors : [];
};
